// The domains' API calls, all in one module (P5-C01 demo slugs and bindings, P5-A01 demo access, P6-C01
// connecting a client domain, P7-A02 and A03 buying and renewing one; docs/API.md «Domains» and «As built
// (Phase 7, domains track)»), typed by the generated schema.
import { queryOptions } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';

export type BindingStatus = Schemas['BindingStatus'];

/**
 * One DNS record a client domain needs (P6-C01): `ownership` (the TXT token) or `routing` (a CNAME to the edge,
 * A/AAAA for the apex), and what the last check saw (null before the first).
 */
export type DnsRecord = Schemas['DnsRecordOut'];

/** What the last checks saw (P6-C01): DNS and TLS apart (AC-09), and the values at the DNS host. */
export type DomainCheck = Schemas['DomainCheckOut'];

/** The step a client domain's check stopped at when it `failed` (P6-C01: DNS, then TLS, then the health check). */
export type FailedStep = 'pending_dns' | 'pending_tls' | 'verified';

/**
 * One binding: reserved → active; an old demo address is `redirect` (to `redirect_to`), a demo after production
 * `gone`. A client domain carries its DNS host (from NS), how the apex is served, why it failed and since when it
 * is in its status (the 48 h DNS alert).
 */
export type DomainBinding = Schemas['DomainBindingOut'];

/** GET /admin/domains/{id}: a client domain with its records and the last check. */
export type CustomDomain = Schemas['CustomDomainOut'];

/** The demo card: the current demo address and its access (`zone` is null while the demo zone is not configured). */
export type DemoDomain = Schemas['DemoDomainOut'];
export type DemoAccess = Schemas['DemoAccessOut'];

/** GET /admin/websites/{id}/domains: the demo card and every binding, newest first (old demo addresses kept as redirects). */
export type WebsiteDomains = Schemas['WebsiteDomainsOut'];

export const domainsKey = (siteId: string) => ['domains', siteId] as const;

export const domainsQuery = (siteId: string) =>
  queryOptions({
    queryKey: domainsKey(siteId),
    queryFn: async (): Promise<WebsiteDomains> => unwrap(api.GET('/admin/websites/{website_id}/domains', { params: { path: { website_id: siteId } } })),
    retry: false,
    // The beat polls a client domain's DNS (P6-C01): the card follows while it is checked.
    refetchInterval: (q) => {
      const b = productionBinding(q.state.data);
      return b?.status === 'pending_purchase' ? 3_000 : isChecking(b) ? 10_000 : false;
    },
  });

/** The website's current demo address, if any. */
export function demoOf(d: WebsiteDomains | undefined): DemoDomain | null {
  return d?.demo ?? null;
}

/** The website's production binding (custom or purchased), if any. */
export function productionBinding(d: WebsiteDomains | undefined): DomainBinding | null {
  return d?.bindings.find((b) => b.channel === 'production' && b.status !== 'gone' && b.status !== 'redirect') ?? null;
}

/** The demo zone the demo slugs live under when the API names none («*.demo.studio.uz»). */
export const DEFAULT_DEMO_ZONE = 'demo.studio.uz';

/** «sharq-v2» from «sharq-v2.demo.studio.uz». */
export function slugOf(hostname: string): string {
  return hostname.split('.')[0] ?? hostname;
}

/** «demo.studio.uz» from «sharq-v2.demo.studio.uz». */
export function zoneOf(hostname: string | null | undefined): string {
  if (!hostname) return DEFAULT_DEMO_ZONE;
  const i = hostname.indexOf('.');
  return i >= 0 ? hostname.slice(i + 1) : DEFAULT_DEMO_ZONE;
}

/** A DNS label: 1–63 of a–z, 0–9 and inner hyphens (P5-C01 also refuses reserved words and short labels). */
export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * The edit-slug action: PUT /admin/websites/{id}/demo-slug {slug} → the demo card. 409 `slug_taken`; 422
 * `slug_invalid*` / `slug_reserved` (P5-C01: no slug is rewritten silently; a live demo's old address redirects).
 */
export async function setDemoSlug(siteId: string, slug: string): Promise<DemoDomain> {
  return unwrap(api.PUT('/admin/websites/{website_id}/demo-slug', { params: { path: { website_id: siteId } }, body: { slug } }));
}

/**
 * The demo's access link (PLAN D22, P5-A01): PUT /admin/websites/{id}/demo-access {enabled, rotate?} → the demo
 * card. It is forced on while the live build shows an image without consent; `rotate` ends every link given so far.
 */
export async function setDemoAccess(siteId: string, enabled: boolean, rotate = false): Promise<DemoDomain> {
  return unwrap(api.PUT('/admin/websites/{website_id}/demo-access', { params: { path: { website_id: siteId } }, body: rotate ? { enabled, rotate } : { enabled } }));
}

/** A hostname as typed: lowercase, without a scheme, path, port or trailing dot. */
export function normalizeHost(input: string): string {
  let v = input.trim().toLowerCase();
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  v = v.split(/[/?#]/)[0] ?? '';
  return v.replace(/:\d+$/, '').replace(/\.$/, '');
}

/** A client hostname: DNS labels, at least one dot, a letter TLD (the API checks it again, and the NS records). */
export const HOST_RE = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * «Подключить свой» → «Проверить» (P6-C01): POST /admin/websites/{id}/domains/connect {hostname} → 201 the client
 * domain at `pending_dns` with the records to add (the TXT token; a CNAME to the edge for a subdomain, A/AAAA for
 * the apex); the first check runs at once. 422 `domain_invalid` (`params.reason`), `domain_reserved`; 409
 * `domain_taken`, `domain_limit_reached`, `website_archived`; 502 `edge_unavailable`.
 */
export function connectDomain(siteId: string, hostname: string): Promise<CustomDomain> {
  return unwrap(api.POST('/admin/websites/{website_id}/domains/connect', { params: { path: { website_id: siteId } }, body: { hostname } }));
}

/** POST /admin/domains/{id}/verify → 202 {job_id, domain}: check now (`domain.verify`); a failed domain starts over from DNS. */
export function verifyDomain(bindingId: string): Promise<Schemas['DomainVerifyStarted']> {
  return unwrap(api.POST('/admin/domains/{domain_id}/verify', { params: { path: { domain_id: bindingId } } }));
}

export const domainKey = (id: string) => ['domains', 'one', id] as const;

/** GET /admin/domains/{id}: the records and the last check; polled while DNS or TLS is checked. */
export const domainQuery = (id: string) =>
  queryOptions({
    queryKey: domainKey(id),
    queryFn: (): Promise<CustomDomain> => unwrap(api.GET('/admin/domains/{domain_id}', { params: { path: { domain_id: id } } })),
    refetchInterval: (q) => (isChecking(q.state.data ?? null) ? 10_000 : false),
    retry: false,
  });

/** The step a failed binding stopped at, read from its DNS and TLS states (AC-09 keeps them apart). */
export function failedStep(b: DomainBinding | null): FailedStep | null {
  if (!b || b.status !== 'failed') return null;
  if (b.dns_state !== 'ok') return 'pending_dns';
  if (b.tls_state !== 'active' && b.tls_state !== 'ok') return 'pending_tls';
  return 'verified';
}

/** A binding still on its way: bought and not registered yet, or checked by the beat (the tab polls it). */
export function isChecking(b: DomainBinding | null): boolean {
  return !!b && (b.status === 'pending_purchase' || b.status === 'pending_dns' || b.status === 'pending_tls');
}

/** The six production-domain states (DESIGN_SPEC §4.5), in order. */
export const DOMAIN_STEPS = ['reserved', 'pending_purchase', 'pending_dns', 'pending_tls', 'verified', 'active'] as const;

/** The production lifecycle's current step (−1 before a binding exists); a failed binding sits on the step it failed. */
export function domainStep(b: DomainBinding | null): number {
  if (!b) return -1;
  const failed = failedStep(b);
  return DOMAIN_STEPS.indexOf((failed ?? b.status) as (typeof DOMAIN_STEPS)[number]);
}

// ── Buying a domain (P7-A02, P7-A03: search, purchase, renewal) ──

/** A price from the registrar price table (S4: no registrar has a quote API), in UZS, with its date. */
export type Quote = Schemas['QuoteOut'];

/** One search row: free, taken, a zone the registrar does not sell, or unknown (an operator checks); `ours`: already a binding in Studio. */
export type SearchResult = Schemas['SearchResultOut'];

/** The configured registrar; `operator`: the assisted path (ahost.uz has no API, an operator registers). */
export type RegistrarInfo = Schemas['RegistrarOut'];

/** The caller's domain budget this month (PLAN D23: 500 000 UZS for a PM; an Admin: `limit` null). */
export type DomainBudget = Schemas['DomainBudgetOut'];

export type DomainSearchOut = Schemas['DomainSearchOut'];

/** The domain's owner: the client, never the platform (S4 §3, the registry's annexes 1 and 2). Never stored in the browser. */
export type OwnerIn = Schemas['OwnerIn'];

export type OrderStatus = Schemas['OrderStatus'];

/** A registration (`register`) or a renewal (`renew`) (`domain_orders`); a registered purchase carries its `renewal` window. */
export type DomainOrder = Schemas['DomainOrderOut'];

export type OrderStarted = Schemas['DomainOrderStartOut'];

/** Orders still on their way (the tab follows them). */
export const ORDER_RUNNING: OrderStatus[] = ['confirmed', 'submitting', 'submitted', 'registry_pending', 'operator_pending'];

/** POST /admin/websites/{id}/domains/search {query} (`domain:read`): availability, prices and the caller's budget. */
export const domainSearchQuery = (siteId: string, query: string) =>
  queryOptions({
    queryKey: ['domains', siteId, 'search', query] as const,
    queryFn: (): Promise<DomainSearchOut> => unwrap(api.POST('/admin/websites/{website_id}/domains/search', { params: { path: { website_id: siteId } }, body: { query } })),
    enabled: query.length > 0,
    staleTime: 30_000,
    retry: false,
  });

/**
 * «Купить» (`domain:purchase`): POST /admin/websites/{id}/domains/purchase with the explicit confirmation, the
 * client's authorisation, the owner and the amount the PM was shown, and an `Idempotency-Key` per opening (a
 * double click buys once). 202 {order, job_id, duplicate}; 402 `budget_exceeded` (`kind = domains`); 409
 * `quote_changed`, `domain_not_available`, `domain_order_exists`; 422 `confirmation_required`, `owner_invalid`.
 */
export function purchaseDomain(siteId: string, key: string, body: Schemas['DomainPurchaseIn'] & { confirm: true }): Promise<OrderStarted> {
  return unwrap(api.POST('/admin/websites/{website_id}/domains/purchase', { params: { path: { website_id: siteId }, header: { 'Idempotency-Key': key } }, body }));
}

/** «Продлить» (P7-A03): the same rules as a purchase. 409 `domain_not_registered`, `domain_expired`. */
export function renewDomain(orderId: string, key: string, body: Schemas['DomainRenewIn'] & { confirm: true }): Promise<OrderStarted> {
  return unwrap(api.POST('/admin/domain-orders/{order_id}/renew', { params: { path: { order_id: orderId }, header: { 'Idempotency-Key': key } }, body }));
}

export const ordersKey = (siteId: string) => ['domains', siteId, 'orders'] as const;

/** GET /admin/websites/{id}/domain-orders: purchases and renewals, newest first; polled while one runs. */
export const ordersQuery = (siteId: string) =>
  queryOptions({
    queryKey: ordersKey(siteId),
    queryFn: async (): Promise<DomainOrder[]> =>
      (await unwrap(api.GET('/admin/websites/{website_id}/domain-orders', { params: { path: { website_id: siteId }, query: { size: 50 } } }))).items,
    refetchInterval: (q) => (q.state.data?.some((o) => ORDER_RUNNING.includes(o.status)) ? 3_000 : false),
    retry: false,
  });

// ── The operator's queue (P7-B02): the assisted path (ahost.uz has no API) and orders nobody could confirm ──

/** What waits for a person: `operator-result` settles these. */
export const OPERATOR_WAITING: OrderStatus[] = ['operator_pending', 'action_required'];

/** An operator's record: `registered` (or renewed) with the new expiry and the registrar's reference, or `refused` (refunded once). */
export type OperatorResult = Schemas['OperatorResultIn'];

export const operatorQueueKey = ['domain-orders', 'queue'] as const;
export const domainOrderKey = (id: string) => ['domain-orders', 'one', id] as const;

/** GET /admin/domain-orders (`domain:read`): every website's waiting orders, the longest-waiting first. A PM reads them. */
export const operatorQueueQuery = queryOptions({
  queryKey: operatorQueueKey,
  queryFn: async (): Promise<DomainOrder[]> => (await unwrap(api.GET('/admin/domain-orders', { params: { query: { size: 100 } } }))).items,
  retry: false,
});

/** GET /admin/domain-orders/{id}: one order (the drawer's details). */
export const domainOrderQuery = (id: string) =>
  queryOptions({
    queryKey: domainOrderKey(id),
    queryFn: (): Promise<DomainOrder> => unwrap(api.GET('/admin/domain-orders/{order_id}', { params: { path: { order_id: id } } })),
    retry: false,
  });

/**
 * «Записать результат» (Admin, `domain:operate`): POST /admin/domain-orders/{id}/operator-result → the order.
 * `registered` needs `expires_on` and hands a purchase to DNS (P6-C01); `refused` fails it and refunds once.
 * 409 `order_not_awaiting_operator`; 422 `expires_on` missing. Audited with the note.
 */
export function recordOperatorResult(orderId: string, body: OperatorResult): Promise<DomainOrder> {
  return unwrap(api.POST('/admin/domain-orders/{order_id}/operator-result', { params: { path: { order_id: orderId } }, body }));
}

/** The registered purchase of a hostname (for its renewal), if any. */
export function purchaseOf(orders: DomainOrder[] | undefined, hostname: string | null | undefined): DomainOrder | null {
  if (!hostname) return null;
  return orders?.find((o) => o.kind === 'register' && o.domain === hostname && o.status === 'registered') ?? null;
}

/** Reminders start 60 days before expiry (S4 §6): the renewal row shows from then on. */
export const RENEW_FROM_DAYS = 60;
